import { FormResponseValue } from "@tellescope/types-models";
import { getNextField } from "./get-next-field";
import { FormFieldNode, NextFieldLogicOptions } from "./types";

/**
 * Upper bound on nodes visited by a single path walk. Guards against a
 * malformed field graph whose `previousFields` wiring escapes the visited-set
 * cycle check; walks stop silently at the cap rather than recursing forever.
 */
export const MAX_FIELD_PATH_DEPTH = 1000;

export interface FieldPathWalkContext {
  /** Answers keyed by field id, used to resolve conditional branching */
  responsesByFieldId: Record<string, FormResponseValue | undefined>;
  /** Every answer the logic evaluator may reference when testing conditions */
  allResponses: FormResponseValue[];
  logicOptions?: NextFieldLogicOptions;
}

const nextFieldFrom = (node: FormFieldNode, context: FieldPathWalkContext): FormFieldNode | undefined =>
  getNextField(node, context.responsesByFieldId[node.value.id], context.allResponses, context.logicOptions);

/**
 * Collects the nodes reachable from `start` by following `getNextField`, in order,
 * beginning with `start` itself.
 *
 * The path is the one the wizard would actually take given the answers in
 * `context` — conditional branches are resolved, not enumerated. A node already
 * on the path terminates the walk, so cyclic wiring yields a finite path.
 */
export const collectFieldPath = (start: FormFieldNode, context: FieldPathWalkContext): FormFieldNode[] => {
  const walk = (node: FormFieldNode, visited: Set<string>, depth: number): FormFieldNode[] => {
    if (visited.has(node.value.id) || depth >= MAX_FIELD_PATH_DEPTH) return [];
    visited.add(node.value.id);

    const next = nextFieldFrom(node, context);
    return [node, ...(next ? walk(next, visited, depth + 1) : [])];
  };

  return walk(start, new Set<string>(), 0);
};

export interface AdvanceWhileResult {
  /** The first node not satisfying the predicate, or the last node reached when the path ends */
  node: FormFieldNode;
  /** Nodes that satisfied the predicate and were stepped over, in traversal order */
  skipped: FormFieldNode[];
}

/**
 * Steps forward from `start` for as long as `shouldSkip` holds, returning where the
 * walk came to rest along with the nodes passed over.
 *
 * `start` itself is tested, so a start node failing the predicate is returned
 * unchanged with nothing skipped. When the path ends (or revisits a node) while the
 * predicate still holds, the final node reached is returned — callers stay put
 * rather than falling off the end of the form.
 */
export const advanceWhile = (
  start: FormFieldNode,
  shouldSkip: (node: FormFieldNode) => boolean,
  context: FieldPathWalkContext
): AdvanceWhileResult => {
  const walk = (node: FormFieldNode, visited: Set<string>, depth: number): AdvanceWhileResult => {
    if (!shouldSkip(node) || visited.has(node.value.id) || depth >= MAX_FIELD_PATH_DEPTH) {
      return { node, skipped: [] };
    }
    visited.add(node.value.id);

    const next = nextFieldFrom(node, context);
    if (!next) return { node, skipped: [] };

    const rest = walk(next, visited, depth + 1);
    return { node: rest.node, skipped: [node, ...rest.skipped] };
  };

  return walk(start, new Set<string>(), 0);
};
